import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PdfFocusProvider, PDFViewer } from "@llamaindex/pdf-viewer";
import "@llamaindex/pdf-viewer/index.css";
import { Badge, Group, Image, Stack, Table } from "@mantine/core";
import { AppModal } from "../../shared/AppModal";
import { FileDownloadOffOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import Papa from "papaparse";
import { useEffect, useMemo, useRef, useState } from "react";
import { DocumentViewer } from "react-documents";
export const FilePreviewModal = ({ filePath, fileType, tags, opened, close }) => {
    const videoRef = useRef(null);
    const [isVertical, setIsVertical] = useState(false);
    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            const checkOrientation = () => {
                setIsVertical(video.videoHeight > video.videoWidth);
            };
            // Check when metadata is loaded
            video.addEventListener("loadedmetadata", checkOrientation);
            // Also check when the video actually starts playing
            video.addEventListener("play", checkOrientation);
            return () => {
                video.removeEventListener("loadedmetadata", checkOrientation);
                video.removeEventListener("play", checkOrientation);
            };
        }
    }, [opened]);
    const isVideoFile = fileType.includes("mp4") ||
        fileType.includes("mov") ||
        fileType.includes("avi") ||
        fileType.includes("wmv") ||
        fileType.includes("flv") ||
        fileType.includes("mkv");
    const isImageFile = fileType.includes("jpg") ||
        fileType.includes("jpeg") ||
        fileType.includes("png") ||
        fileType.includes("gif") ||
        fileType.includes("bmp") ||
        fileType.includes("tiff") ||
        fileType.includes("ico") ||
        fileType.includes("webp");
    const isCsvFile = fileType.includes("csv");
    const isPdfFile = fileType.includes("pdf");
    const isDocumentFile = fileType.includes("docx") ||
        fileType.includes("doc") ||
        fileType.includes("pptx") ||
        fileType.includes("ppt");
    const isNotSupportedFile = fileType.includes("zip") ||
        fileType.includes("rar") ||
        fileType.includes("7z") ||
        fileType.includes("tar") ||
        fileType.includes("gz") ||
        fileType.includes("bz2") ||
        fileType.includes("xls") ||
        fileType.includes("xlsx");
    // Drop nullish/empty entries so we don't render blank badges
    const visibleTags = useMemo(() => (tags ?? []).filter((tag) => typeof tag === "string" && tag.length > 0), [tags]);
    return (_jsx(AppModal, { variant: isVideoFile
            ? isVertical
                ? "preview-vertical"
                : "preview-wide"
            : isNotSupportedFile
                ? "preview-compact"
                : "preview", size: "xl", title: "File Preview", opened: opened, onClose: close, children: _jsxs(Stack, { gap: "sm", children: [isVideoFile
                    ? videoViewer(videoRef, filePath, isVertical, setIsVertical)
                    : isImageFile
                        ? imageViewer(filePath)
                        : isPdfFile
                            ? pdfViewer(filePath)
                            : isDocumentFile
                                ? documentViewer(filePath)
                                : isCsvFile
                                    ? csvViewer(filePath)
                                    : notSupportedFileViewer(), visibleTags.length > 0 && (_jsx(Group, { gap: 6, wrap: "wrap", children: visibleTags.map((tag) => (_jsx(Badge, { size: "sm", color: "var(--mantine-color-secondary-5)", children: tag }, tag))) }))] }) }));
};
const videoViewer = (videoRef, filePath, isVertical, setIsVertical) => {
    return (_jsx("video", { ref: videoRef, src: filePath, controls: true, onLoadedMetadata: (e) => {
            const video = e.currentTarget;
            setIsVertical(video.videoHeight > video.videoWidth);
        }, style: {
            maxWidth: "100%",
            maxHeight: "80vh",
            width: isVertical ? "80rem" : "100%",
            height: isVertical ? "100%" : "auto",
            objectFit: "contain"
        } }));
};
const imageViewer = (filePath) => {
    return (_jsx(Image, { src: filePath, style: { maxWidth: "50rem", maxHeight: "37rem" }, fit: "contain" }));
};
const csvViewer = (filePath) => {
    return _jsx(SimpleCSVViewer, { url: filePath });
};
const pdfViewer = (filePath) => {
    return (_jsx(PdfFocusProvider, { children: _jsx(PDFViewer, { file: { id: "PDF Preview", url: filePath } }) }));
};
const documentViewer = (filePath) => {
    return (_jsx(DocumentViewer, { style: { width: "46rem", height: "60rem" }, url: filePath, viewer: "google" }));
};
const notSupportedFileViewer = () => {
    return (_jsxs("div", { style: {
            width: "24vw",
            height: "25vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "var(--mantine-color-gray-0)",
            borderRadius: "12px",
            border: "1px solid var(--mantine-color-gray-3)",
            textAlign: "center",
            padding: "1rem"
        }, children: [_jsx(SvgIcon, { component: FileDownloadOffOutlined, style: {
                    fontSize: "2rem",
                    color: "var(--mantine-color-gray-5)",
                    marginBottom: "0.5rem"
                } }), _jsx("p", { style: {
                    margin: 0,
                    fontSize: "1rem",
                    color: "var(--mantine-color-gray-7)"
                }, children: "This file type is not supported for preview." })] }));
};
// This is a simple CSV viewer that uses the react-papaparse library to parse the CSV file and display it in a table.
export const SimpleCSVViewer = ({ url }) => {
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    useEffect(() => {
        const fetchAndParseCSV = async () => {
            try {
                const response = await fetch(url);
                const text = await response.text();
                Papa.parse(text, {
                    complete: (result) => {
                        if (result.errors.length) {
                            setError("Error parsing CSV file.");
                        }
                        else {
                            setRows(result.data);
                        }
                    },
                    error: () => {
                        setError("Failed to parse CSV.");
                    }
                });
            }
            catch (err) {
                setError("Failed to load CSV file.");
            }
        };
        fetchAndParseCSV();
    }, [url]);
    if (error)
        return _jsx("p", { children: error });
    if (!rows.length)
        return _jsx("p", { children: "Loading..." });
    return (_jsx("div", { style: { overflow: "auto", maxHeight: "80vh" }, children: _jsxs(Table, { style: { borderCollapse: "collapse", width: "100%" }, children: [_jsx(Table.Thead, { children: _jsx(Table.Tr, { children: rows[0].map((header, i) => (_jsx(Table.Th, { style: {
                                border: "1px solid var(--mantine-color-gray-4)",
                                padding: "8px",
                                background: "var(--mantine-color-gray-1)"
                            }, children: header }, `header-${i}`))) }) }), _jsx(Table.Tbody, { children: rows.slice(1).map((row, rowIndex) => (_jsx(Table.Tr, { children: row.map((cell, cellIndex) => (_jsx(Table.Td, { style: {
                                border: "1px solid var(--mantine-color-gray-3)",
                                padding: "8px"
                            }, children: cell }, `cell-${rowIndex}-${cellIndex}`))) }, `row-${rowIndex}`))) })] }) }));
};
